import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Zap, Trophy, Clock } from 'lucide-react';
import ParticleHero from '@/components/ParticleHero';
import { TileImage } from '@/components/common/Skeleton';
import SocialProof from '@/components/home/SocialProof';
import HomeMusic from '@/components/home/HomeMusic';
import HomeCommunityPreview from '@/components/home/HomeCommunityPreview';
import FeaturedVideo from '@/components/FeaturedVideo';
import ArtistStory from '@/components/ArtistStory';
import WhatYoureMissing from '@/components/home/WhatYoureMissing';
import WinbackBanner from '@/components/home/WinbackBanner';
import HomeShopStrip from '@/components/home/HomeShopStrip';
import RankMedals from '@/components/home/RankMedals';
import { TipWidget } from '@/components/tips/TipWidget';
import { JoinCta, useIsMember } from '@/components/common/JoinCta';
import { applySeo, canonicalFor, musicGroupSchema, websiteSchema, eventSchema } from '@/lib/seo';

import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ActiveContestBanner = () => {
  const { t } = useLanguage();
  const [contest, setContest] = useState(null);

  useEffect(() => {
    axios.get(`${API}/contests`).then(r => {
      const active = r.data.find(c => c.status === 'active' || c.status === 'ending_soon');
      if (active) setContest(active);
    }).catch(() => {});
  }, []);

  if (!contest) return null;

  const isEndingSoon = contest.status === 'ending_soon';
  const statusColor = isEndingSoon ? 'var(--error)' : 'var(--success)';

  return (
    <section data-testid="active-contest-banner" className="relative py-12 overflow-hidden" style={{ background: 'var(--accent-light)' }}>
      <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(ellipse at 50% 50%, var(--glass-shadow), transparent 70%)' }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="flex flex-col md:flex-row items-center gap-6 md:gap-12">
          {contest.image && (
            <div className="relative w-32 h-32 md:w-40 md:h-40 rounded-3xl overflow-hidden flex-shrink-0 border" style={{ borderColor: 'var(--border-color)' }}>
              <TileImage src={contest.image} alt={contest.name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
              {isEndingSoon ? <Clock size={16} style={{ color: statusColor }} /> : <Zap size={16} style={{ color: statusColor }} />}
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: statusColor }}>
                {isEndingSoon ? `${t('contests.endingSoon')} ${contest.hours_remaining}${t('contests.hoursLeft')}` : t('contests.liveBanner')}
              </span>
            </div>
            <h2 className="text-h3" style={{ color: 'var(--text-primary)' }}>{contest.name}</h2>
            {contest.prize && (
              <p className="mt-1 text-lg font-semibold flex items-center gap-2 justify-center md:justify-start" style={{ color: 'var(--accent-primary)' }}>
                <Trophy size={18} />{contest.prize}
              </p>
            )}
          </div>
          <Link to={`/contests/${contest.slug}`} data-testid="contest-banner-cta" className="btn-pill btn-primary">
            {t('contests.winAPrize')}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const HomeTipSection = () => {
  const { t, lang } = useLanguage();
  const isMember = useIsMember();
  const [header, setHeader] = useState('');
  useEffect(() => {
    axios.get(`${API}/home/tip-widget`, { params: { lang } }).then((r) => { if (r.data?.header_text) setHeader(r.data.header_text); }).catch(() => {});
  }, [lang]);
  return (
    <section id="tip-widget" data-testid="home-tip-section" className="relative py-14" style={{ background: 'var(--bg-primary)' }}>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center flex flex-col items-center gap-5">
        {isMember ? (
          <>
            <div>
              <h2 className="text-h2 font-bold" style={{ color: 'var(--text-primary)' }} data-testid="tip-section-header">{header || t('home.tipHeaderFallback')}</h2>
            </div>
            <TipWidget sourceType="homepage" />
          </>
        ) : (
          <JoinCta source="home_footer" />
        )}
      </div>
    </section>
  );
};

const REORDERABLE_KEYS = ["social_proof", "artist_history", "featured_video", "community_preview", "music_section", "rank_medals", "shop_strip", "what_you_re_missing", "contest_banner", "tip_widget"];

const Home = () => {
  const [vis, setVis] = useState(null);
  const [order, setOrder] = useState(REORDERABLE_KEYS);
  const [seo, setSeo] = useState({ avatar: null, contest: null });

  useEffect(() => {
    axios.get(`${API}/home/section-visibility`).then((r) => setVis(r.data)).catch(() => setVis({}));
    axios.get(`${API}/home/section-order`).then((r) => setOrder(r.data.order || REORDERABLE_KEYS)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/artist/profile`).then((r) => r.data).catch(() => null),
      axios.get(`${API}/contests`).then((r) => r.data).catch(() => null),
    ]).then(([artist, contests]) => {
      const active = Array.isArray(contests) ? contests.find((c) => c.status === 'active' || c.status === 'ending_soon') : null;
      setSeo({ avatar: artist?.avatar_url || null, contest: active || null });
    });
  }, []);

  useEffect(() => {
    const jsonLd = [musicGroupSchema(seo.avatar), websiteSchema()];
    if (seo.contest && seo.contest.name) {
      jsonLd.push(eventSchema({
        name: seo.contest.name,
        startDate: seo.contest.start_date || seo.contest.starts_at,
        endDate: seo.contest.end_date || seo.contest.ends_at,
        description: seo.contest.description,
      }));
    }
    return applySeo({
      title: "SAMTWO - Watch TV Shows Online, Stream Music Online",
      description: "The official home of SAMTWO. Stream the full catalog, watch exclusive videos, and join the community.",
      canonical: canonicalFor("/"),
      jsonLd,
    });
  }, [seo]);

  // Default to visible (true) for any missing/loading key so nothing flashes hidden.
  const show = (key) => !vis || vis[key] !== false;

  const COMPONENTS = {
    social_proof: <SocialProof />,
    artist_history: <ArtistStory />,
    featured_video: <FeaturedVideo />,
    community_preview: <HomeCommunityPreview />,
    music_section: <HomeMusic />,
    rank_medals: <RankMedals />,
    shop_strip: <HomeShopStrip />,
    what_you_re_missing: <WhatYoureMissing />,
    // Contests feature is dark (clipping moved to /clipping); banner hidden but kept for reuse.
    contest_banner: null,
    tip_widget: <HomeTipSection />,
  };

  return (
    <main data-testid="home-page">
      <ParticleHero />
      <WinbackBanner />
      {order.map((key) => (
        <React.Fragment key={key}>{show(key) && COMPONENTS[key]}</React.Fragment>
      ))}
    </main>
  );
};

export default Home;
